import React from 'react'
import { Button, Input, Label } from '@/shared/ui'
import { getRevshareMonthConfig, upsertRevshareMonthConfig } from '../services/revshareSummaryService'
import { bpsToPercent, percentToBps } from '../utils/percent'

type Props = {
  monthStartDate: string
  refreshKey: number
}

export default function RevshareMonthConfigTab({ monthStartDate, refreshKey }: Props) {
  const [poolCapPercent, setPoolCapPercent] = React.useState<number>(0)
  const [paymentDate, setPaymentDate] = React.useState<string>('')
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const cfg = await getRevshareMonthConfig(monthStartDate)
      const cap = cfg ? Number(cfg.pool_cap_bps || 0) : 0
      setPoolCapPercent(cap / 100)
      setPaymentDate(cfg?.payment_date ? String(cfg.payment_date) : '')
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar configuracao')
      setPoolCapPercent(0)
      setPaymentDate('')
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthStartDate, refreshKey])

  async function onSave() {
    setSaving(true)
    setError(null)
    try {
      await upsertRevshareMonthConfig({
        competence_month: monthStartDate,
        pool_cap_bps: percentToBps(poolCapPercent),
        payment_date: paymentDate || null,
      })
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar configuracao')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-[900px] space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="text-base font-medium">Configuracao do mes</div>
        <p className="mt-1 text-sm text-foreground/70">
          Define o teto da pool (percentual da receita liquida) e a data sugerida de pagamento do mes.
        </p>

        {error && (
          <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Teto da pool (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={poolCapPercent}
              onChange={(e) => setPoolCapPercent(Number((e.target as HTMLInputElement).value || 0))}
              className="h-10"
            />
            <div className="text-xs text-foreground/60">
              Atual: <span className="font-mono">{bpsToPercent(percentToBps(poolCapPercent), 2)}</span>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Data sugerida de pagamento</Label>
            <Input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate((e.target as HTMLInputElement).value)}
              className="h-10"
            />
            <div className="text-xs text-foreground/60">Opcional. Usado como default ao criar payout do mes.</div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={onSave} disabled={loading || saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  )
}

